import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Search, Loader2, X, Database } from 'lucide-react';
import { Badge } from './ui/badge';

const CidSearch = ({ value, onChange, placeholder = "Pesquisar CID-10 (código ou descrição)...", api }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalCodes, setTotalCodes] = useState(0);
  const wrapperRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function using API with debounce
  const searchCID = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    
    try {
      if (api) {
        const response = await api.get(`/cid10/search?q=${encodeURIComponent(term)}&limit=30`);
        if (response.data) {
          setResults(response.data.results || []);
          if (response.data.total > 0) {
            setTotalCodes(response.data.total);
          }
        }
      } else {
        // Fallback to local search if no API
        const response = await fetch('/cid10_database.json');
        const data = await response.json();
        
        const normalizedTerm = term.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        const filtered = data.filter(cid => {
          const normalizedDesc = cid.description.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
          const normalizedCode = cid.code.toLowerCase();
          
          return normalizedCode.includes(normalizedTerm) || 
                 normalizedDesc.includes(normalizedTerm);
        }).slice(0, 30);

        setResults(filtered);
        setTotalCodes(data.length);
      }
    } catch (err) {
      console.error('CID search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsOpen(true);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchCID(term);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle selection
  const handleSelect = (cid) => {
    const selectedValue = `${cid.code} - ${cid.description}`;
    setSearchTerm(selectedValue);
    onChange(cid.code);
    setIsOpen(false);
  };

  // Clear selection
  const handleClear = () => {
    setSearchTerm('');
    onChange('');
    setResults([]);
  };

  // Initialize with existing value
  useEffect(() => {
    const initializeValue = async () => {
      if (value && !searchTerm) {
        // Try to get full code details from API
        if (api) {
          try {
            const response = await api.get(`/cid10/code/${value}`);
            if (response.data?.found) {
              setSearchTerm(`${response.data.code} - ${response.data.description}`);
              return;
            }
          } catch (err) {
            // CID details fetch failed silently
          }
        }
        // Fallback to just the code
        setSearchTerm(value);
      }
    };
    initializeValue();
  }, [value, api]);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-10 font-mono"
          data-testid="cid-search-input"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && (searchTerm.length >= 2) && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Pesquisando...</span>
            </div>
          ) : results.length > 0 ? (
            <>
              <ScrollArea className="max-h-72">
                {results.map((cid, index) => (
                  <button
                    key={cid.code}
                    onClick={() => handleSelect(cid)}
                    className={`
                      w-full px-4 py-2.5 text-left hover:bg-muted transition-colors
                      ${index !== results.length - 1 ? 'border-b' : ''}
                    `}
                    data-testid={`cid-option-${cid.code}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-primary">{cid.code}</span>
                      <span className="text-muted-foreground">-</span>
                      <span className="text-sm flex-1 truncate">{cid.description}</span>
                    </div>
                    {cid.group && (
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {cid.group}
                      </div>
                    )}
                  </button>
                ))}
              </ScrollArea>
              <div className="px-3 py-1.5 border-t bg-muted/50 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Database className="h-3 w-3" />
                  {results.length} resultado(s)
                </span>
                <Badge variant="outline" className="text-[10px] h-4">
                  CID-10 Completo
                </Badge>
              </div>
            </>
          ) : (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Nenhum CID encontrado para "{searchTerm}"
              <p className="text-xs mt-1">Tente buscar por código (ex: K80) ou doença (ex: colelitíase)</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CidSearch;
